import { CaretDownFilled } from "@ant-design/icons";
import { Flex, Tag, Typography } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { AI_SUGGESTIONS_STATE_KEY, BOT_NAME } from "../constants/AppConstants";
import useAI from "../hooks/useAI";
import useEscHandler from "../hooks/useEscHandler";
import { DEFAULT_CONTACT_ID } from "./ConversationContext";

const { Text } = Typography;

const saveExpandedState = (isExpanded) => {
  try {
    window.localStorage.setItem(AI_SUGGESTIONS_STATE_KEY, JSON.stringify(isExpanded));
  } catch (error) {
    console.error("Error saving AI suggestions state to localStorage:", error);
  }
};

function AiAutoSuggestShowArea({
  appProperties,
  onApiCallStart,
  onApiCallComplete,
  onSuggestionAccept,
  contactId,
  isExpanded,
  setIsExpanded,
  conversations = [],
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { getSuggestedRepliesForContact } = useAI();

  // Track the last processed incoming message ID to avoid duplicate refreshes
  const lastProcessedMessageIdRef = useRef(null);
  // Track the last contactId we fetched for to avoid refetching on same contact
  const lastFetchedContactIdRef = useRef(null);
  // AbortController to cancel in-flight requests
  const abortControllerRef = useRef(null);

  useEffect(() => {
    saveExpandedState(isExpanded);
  }, [isExpanded]);

  const fetchSuggestions = useCallback(async () => {
    if (!isExpanded) return;
    
    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    setIsLoading(true);
    setSuggestions([]);
    onApiCallStart?.();

    try {
      const response = await getSuggestedRepliesForContact(contactId, appProperties, controller.signal);
      const suggestionsData = response?.suggestions || [];
      setSuggestions(suggestionsData);
      lastFetchedContactIdRef.current = contactId; // Track that we fetched for this contact
      onApiCallComplete?.(suggestionsData);
    } catch (error) {
      // Ignore abort/cancel errors (axios uses ERR_CANCELED, native fetch uses AbortError)
      if (controller.signal.aborted || error.name === 'AbortError' || error.code === 'ERR_CANCELED' || error.message === 'canceled') {
        return;
      }
      console.error("Error fetching suggestions:", error);
      onApiCallComplete?.([]);
    } finally {
      const lastMessageId = conversations[conversations.length - 1]?.id ||conversations[conversations.length - 1]?.messageId || conversations[conversations.length - 1]?.internalMessageId ;
      lastProcessedMessageIdRef.current = lastMessageId;
      // Only update loading state if this request wasn't aborted
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [isExpanded, onApiCallStart, getSuggestedRepliesForContact, contactId, appProperties, onApiCallComplete, conversations]);

  // Fetch suggestions when panel is expanded and contactId is available (only if not already fetched for this contact)
  useEffect(() => {
    const lastMessageId = conversations[conversations.length - 1]?.id ||conversations[conversations.length - 1]?.messageId || conversations[conversations.length - 1]?.internalMessageId ;
    if (appProperties?.isAutoSuggestionEnabled && contactId && isExpanded && (lastFetchedContactIdRef.current !== contactId || lastProcessedMessageIdRef.current !== lastMessageId)) {
      fetchSuggestions();
    }
  }, [isExpanded, contactId, appProperties?.isAutoSuggestionEnabled, fetchSuggestions, conversations, lastFetchedContactIdRef, lastProcessedMessageIdRef]);

  // Reset fetched contact and cancel in-flight requests when contactId changes
  useEffect(() => {
    if (contactId && lastFetchedContactIdRef.current !== contactId) {
      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      lastFetchedContactIdRef.current = null;
      lastProcessedMessageIdRef.current = null;
      setSuggestions([]);
      setIsLoading(false);
    }
  }, [contactId]);

  // Cleanup: abort request on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // // Watch for new incoming messages and refresh suggestions
  // useEffect(() => {
  //   // Only process if AI is enabled, contactId exists, panel is expanded, and conversations are available
  //   if (!appProperties?.isAutoSuggestionEnabled || !contactId || !isExpanded || !conversations || conversations.length === 0) {
  //     return;
  //   }

  //   // Filter for incoming messages (direction === 1)
  //   const incomingMessages = conversations.filter((msg) => msg?.direction === 1);

  //   if (incomingMessages.length === 0) {
  //     return;
  //   }

  //   // Find the most recent incoming message
  //   // Sort by logTime or use the last item in the array (assuming conversations are sorted chronologically)
  //   const mostRecentIncomingMessage = incomingMessages.reverse().find((message) => message.direction === 1);

  //   // Get message ID (prefer messageId, fallback to internalMessageId or id)
  //   const messageId = mostRecentIncomingMessage?.messageId || mostRecentIncomingMessage?.internalMessageId || mostRecentIncomingMessage?.id;
  //   // Check if this is a new message we haven't processed yet
  //   if (messageId && messageId !== lastProcessedMessageIdRef.current) {
  //     // Update the tracked message ID
  //     lastProcessedMessageIdRef.current = messageId;

  //     // Trigger refresh of suggestions
  //     fetchSuggestions();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [conversations, contactId, appProperties?.isAutoSuggestionEnabled]);

  const handleSuggestionClick = (suggestion) => {
    onSuggestionAccept?.(suggestion);
    // Clear suggestions after accepting
    setIsExpanded(false);
  };

  const handleToggleExpand = () => {
    if (isExpanded) {
      // Start collapse animation
      setIsCollapsing(true);
      // After animation completes, switch to collapsed state
      setTimeout(() => {
        setIsExpanded(false);
        setIsCollapsing(false);
      }, 300);
    } else {
      setIsExpanded(true);
    }
  };

  // Register ESC handler when expanded
  useEscHandler(handleToggleExpand, isExpanded && !isCollapsing);

  if (!isExpanded || !contactId || contactId === DEFAULT_CONTACT_ID) return null;

  // Expanded state: Show full suggestions panel
  return (
    <div className={`p-3 ai-background-effect w-100 ai-suggestions-expanded ${isCollapsing ? "ai-suggestions-collapsing" : ""}`}>
      <Flex justify="space-between" align="center" className="mb-2">
        <Flex align="center" gap={8}>
          <div className="actionIconsSprite ai-bot-icon-expanded position-relative top-0" />
          <Text className="hs-fs-12 hs-fw-600 hs-color-dark-grey">{BOT_NAME}'s Suggestions</Text>
        </Flex>
        <CaretDownFilled className="cursor-pointer" onClick={handleToggleExpand} />
      </Flex>
      <Flex wrap="wrap" gap={8} className="w-100 hs-pill-container">
        {suggestions.map((suggestion, index) => (
          <Tag
            key={index}
            className="hs-pill hs-pill-violet hs-pill-animated cursor-pointer"
            onClick={() => handleSuggestionClick(suggestion)}
            style={{ "--pill-index": String(index) }}
          >
            {suggestion}
          </Tag>
        ))}
        {isLoading && (
          <div className="w-100 d-flex justify-content-center align-items-center">
            <div className="actionIconsSprite dropDownLoading" />
          </div>
        )}
      </Flex>
    </div>
  );
}

export default AiAutoSuggestShowArea;
